import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Pencil, Trash2, Search } from "lucide-react";
import { toast } from "sonner";
import type { BlogPost } from "@endeks/shared";
import { useBlogPosts, useDeleteBlogPost } from "./queries";
import { showDeleteConfirm } from "../../components/Common/DeleteConfirm";
import { QueryError } from "../../components/Common/QueryError";
import { Pagination } from "../../components/Common/Pagination";
import { errorMessage } from "@/lib/error-message";
import { useDebouncedValue } from "@/lib/use-debounced-value";
import { usePagination } from "@/lib/use-pagination";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

// id is optional in the shared schema but the API always returns it for existing rows.
type BlogRow = BlogPost & { id: string };

export default function BlogList() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search.trim(), 300);
  const {
    data: rawPosts,
    isLoading,
    isError,
    error,
    refetch,
  } = useBlogPosts(debouncedSearch ? { search: debouncedSearch } : undefined);
  const posts: BlogRow[] = (rawPosts ?? []) as BlogRow[];
  const { pageItems, page, totalPages, totalItems, setPage } = usePagination(
    posts,
    20,
    debouncedSearch,
  );
  const deleteMutation = useDeleteBlogPost();

  function handleDelete(id: string) {
    showDeleteConfirm({
      onConfirm: () => {
        deleteMutation.mutate(id, {
          onSuccess: () => toast.success("Yazı silindi"),
          onError: (e: unknown) => toast.error(errorMessage(e, "Silinemedi")),
        });
      },
    });
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Blog Yazıları</h1>
        <Button onClick={() => navigate("/blog/new")}>
          <Plus className="h-4 w-4 mr-2" />
          Yeni Yazı
        </Button>
      </div>

      {/* Search */}
      <div className="relative w-64">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Ara..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-8"
        />
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      ) : isError && !rawPosts ? (
        <QueryError error={error} onRetry={() => void refetch()} />
      ) : posts.length === 0 ? (
        <div className="flex items-center justify-center h-40 text-muted-foreground text-sm">
          {debouncedSearch ? "Arama sonucu bulunamadı." : "Henüz blog yazısı eklenmedi."}
        </div>
      ) : (
        <>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Başlık</TableHead>
                <TableHead>Slug</TableHead>
                <TableHead className="w-24">Etiket</TableHead>
                <TableHead className="w-24">Durum</TableHead>
                <TableHead className="w-32">Tarih</TableHead>
                <TableHead className="w-28 text-right">İşlemler</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pageItems.map((post) => (
                <TableRow key={post.id}>
                  <TableCell className="font-medium max-w-xs truncate">{post.title}</TableCell>
                  <TableCell className="text-muted-foreground text-sm max-w-[180px]">
                    <div className="truncate">{post.slug}</div>
                    {post.slugEn && post.slugEn !== post.slug && (
                      <div className="truncate text-xs text-muted-foreground/70">
                        EN: {post.slugEn}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant="secondary">{post.tag ?? "Blog"}</Badge>
                  </TableCell>
                  <TableCell>
                    {post.published ? (
                      <Badge variant="default">Yayında</Badge>
                    ) : (
                      <Badge variant="outline">Taslak</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-muted-foreground text-sm">
                    {post.publishedAt
                      ? new Date(post.publishedAt).toLocaleDateString("tr-TR")
                      : "-"}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate(`/blog/${post.id}/edit`)}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-destructive hover:text-destructive"
                        onClick={() => handleDelete(post.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Pagination
            page={page}
            totalPages={totalPages}
            totalItems={totalItems}
            onPageChange={setPage}
          />
        </>
      )}
    </div>
  );
}
